import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useWishlistStore } from '@/store/wishlistStore';
import { useCartStore } from '@/store/cartStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { FaHeart } from 'react-icons/fa6';
import { RiShoppingCartLine, RiDeleteBin6Line } from 'react-icons/ri';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export function Wishlist() {
  const { currentLang } = useI18n();
  const navigate = useNavigate();
  const { items, removeItem, clearAll } = useWishlistStore();
  const addToCart = useCartStore((state) => state.addItem);

  const handleAddToCart = (item: typeof items[0]) => {
    addToCart({
      productId: item.productId,
      quantity: 1,
      price: item.price,
      name: item.name,
      nameBn: item.nameBn,
      image: item.image,
      weight: 0,
      stock: 999,
    });
    toast.success(currentLang === 'bn' ? 'কার্টে যোগ হয়েছে' : 'Added to cart');
  };

  const handleRemove = (productId: string) => {
    removeItem(productId);
    toast.success(currentLang === 'bn' ? 'উইশলিস্ট থেকে সরানো হয়েছে' : 'Removed from wishlist');
  };

  return (
    <div className="min-h-[100dvh] bg-gray-50 font-sans pb-32 lg:pb-10">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl hover:bg-gray-50 text-black transition-all active:scale-95"
            onClick={() => navigate(-1)}
          >
            <FaCircleArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-black tracking-tight text-center flex-1">
            {currentLang === 'bn' ? 'উইশলিস্ট' : 'Wishlist'}
            {items.length > 0 && (
              <span className="text-sm font-medium text-gray-400 ml-2"></span>
            )}
          </h1>
          {items.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs font-bold"
              onClick={() => {
                clearAll();
                toast.success(currentLang === 'bn' ? 'সব সরানো হয়েছে' : 'Wishlist cleared');
              }}
            >
              {currentLang === 'bn' ? 'সব মুছুন' : 'Clear All'}
            </Button>
          ) : (
            <div className="w-10" />
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 max-w-lg mt-4">
        <AnimatePresence mode="popLayout">
          {items.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-10 text-center border border-gray-100 shadow-sm mt-4"
            >
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaHeart className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">
                {currentLang === 'bn' ? 'উইশলিস্ট খালি' : 'Your Wishlist is Empty'}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {currentLang === 'bn'
                  ? 'পছন্দের পণ্যগুলো সেভ করে রাখতে হার্ট আইকনে ক্লিক করুন।'
                  : 'Tap the heart icon on items you love to save them for later.'}
              </p>
              <Button
                onClick={() => navigate('/products')}
                className="rounded-full px-8 font-bold h-12 shadow-md shadow-primary/20"
              >
                {currentLang === 'bn' ? 'পণ্য দেখুন' : 'Explore Products'}
              </Button>
            </motion.div>
          ) : (
            <div className="space-y-3">
              {items.map((item, index) => (
                <motion.div
                  key={item.productId}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.25, delay: index * 0.05 }}
                >
                  <Card className="border-none shadow-[0px_1px_4px_rgba(0,0,0,0.15)] overflow-hidden py-0">
                    <div className="flex items-center gap-3 p-3">
                      {/* Product Image */}
                      <button
                        onClick={() => navigate(`/products/${item.productId}`)}
                        className="shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-gray-100"
                      >
                        <img
                          src={item.image || '/placeholder-product.png'}
                          alt={currentLang === 'bn' ? item.nameBn : item.name}
                          className="w-full h-full object-cover"
                        />
                      </button>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => navigate(`/products/${item.productId}`)}
                          className="text-left w-full"
                        >
                          <h3 className="font-bold text-sm text-black truncate">
                            {currentLang === 'bn' ? item.nameBn : item.name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {currentLang === 'bn' ? item.name : item.nameBn}
                          </p>
                        </button>
                        <p className="text-sm font-black text-primary mt-1">
                          ৳{item.price.toFixed(2)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          size="icon"
                          className="h-9 w-9 rounded-xl"
                          onClick={() => handleAddToCart(item)}
                        >
                          <RiShoppingCartLine className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 rounded-xl text-red-400 hover:text-red-500 hover:bg-red-50"
                          onClick={() => handleRemove(item.productId)}
                        >
                          <RiDeleteBin6Line className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
